<section class="overview-page client-management">
  <app-page-header
    [title]="'clientManagement.headline' | translate"
    [subtitle]="'clientManagement.subtitle' | translate"
  >
    <ng-container right>
      <app-stat-group [items]="stats()" />
      <button
        mat-flat-button
        color="primary"
        type="button"
        class="client-management__create-btn"
        (click)="onCreateOpen()"
      >
        <mat-icon aria-hidden="true">add</mat-icon>
        {{ 'clientManagement.add' | translate }}
      </button>
    </ng-container>
  </app-page-header>

  <div class="overview-page__toolbar">
    <app-search-bar
      [placeholder]="'clientManagement.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />

    <button
      type="button"
      class="dropdown-chip"
      [class.dropdown-chip--active]="hostingFilterValue() !== null"
      [matMenuTriggerFor]="hostingMenu"
      [attr.aria-label]="'clientManagement.filter.hosting' | translate"
    >
      @if (hostingFilterValue()) {
        {{ 'clientManagement.hostingMode.' + hostingFilterValue() | translate }}
      } @else {
        {{ 'clientManagement.filter.hosting' | translate }}
      }
      <span class="dropdown-chip__caret" aria-hidden="true">▾</span>
    </button>
    <mat-menu #hostingMenu="matMenu">
      <button mat-menu-item (click)="setHostingFilter(null)">
        {{ 'clientManagement.filter.allHosting' | translate }}
      </button>
      <button mat-menu-item (click)="setHostingFilter('MANAGED')">
        {{ 'clientManagement.hostingMode.MANAGED' | translate }}
      </button>
      <button mat-menu-item (click)="setHostingFilter('SELF_HOSTED')">
        {{ 'clientManagement.hostingMode.SELF_HOSTED' | translate }}
      </button>
    </mat-menu>

    <button
      type="button"
      class="dropdown-chip"
      [class.dropdown-chip--active]="statusFilterValue() !== null"
      [matMenuTriggerFor]="statusMenu"
      [attr.aria-label]="'clientManagement.filter.status' | translate"
    >
      @if (statusFilterValue()) {
        {{ 'clientManagement.status.' + statusFilterValue() | translate }}
      } @else {
        {{ 'clientManagement.filter.status' | translate }}
      }
      <span class="dropdown-chip__caret" aria-hidden="true">▾</span>
    </button>
    <mat-menu #statusMenu="matMenu">
      <button mat-menu-item (click)="setStatusFilter(null)">
        {{ 'clientManagement.filter.allStatus' | translate }}
      </button>
      @for (option of statusOptions; track option) {
        <button mat-menu-item (click)="setStatusFilter(option)">
          {{ 'clientManagement.status.' + option | translate }}
        </button>
      }
    </mat-menu>
  </div>

  @if (loading()) {
    <div class="overview-page__loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (error()) {
    <p class="overview-page__hint">{{ error()! | translate }}</p>
  } @else {
    <div class="overview-page__table-card" appScrollContainer>
      <table
        mat-table
        [dataSource]="pagedCustomers()"
        matSort
        class="overview-page__table"
        (matSortChange)="onSortChange($event)"
      >
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            {{ 'clientManagement.table.customer' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <div class="cell-name">
              <app-avatar [name]="row.name" [size]="34" />
              <span class="cell-name__label">{{ row.name }}</span>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="hostingMode">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'clientManagement.table.hostingMode' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <span class="hosting-pill" [class]="hostingPillClass(row.hostingMode)">
              {{ 'clientManagement.hostingMode.' + row.hostingMode | translate }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="azureTenantId">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'clientManagement.table.azureTenantId' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <span class="cell-tenant-id">{{ row.azureTenantId ?? '—' }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'clientManagement.table.status' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <app-status-pill [kind]="statusKind(row.status)" />
          </td>
        </ng-container>

        <ng-container matColumnDef="details">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'clientManagement.table.users' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <a
              class="client-management__users-chip"
              [routerLink]="['/client-management', row.id]"
              [attr.aria-label]="('clientManagement.ariaLabel.users' | translate) + ': ' + row.name"
            >
              <mat-icon class="client-management__users-chip-icon" aria-hidden="true">people</mat-icon>
              {{ row.userCount ?? '—' }}
            </a>
          </td>
        </ng-container>

        <ng-container matColumnDef="manage">
          <th mat-header-cell *matHeaderCellDef scope="col" class="col-actions">
            {{ 'clientManagement.table.manage' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <div class="overview-page__action-cell">
              <button
                class="overview-page__action-btn overview-page__action-btn--accent"
                type="button"
                (click)="onEditOpen(row)"
                [attr.aria-label]="'clientManagement.ariaLabel.edit' | translate"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
              </button>
              <button
                class="overview-page__action-btn overview-page__action-btn--danger"
                type="button"
                (click)="onDeleteOpen(row)"
                [attr.aria-label]="'clientManagement.ariaLabel.delete' | translate"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" class="overview-page__row"></tr>

        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell" colspan="6">{{ 'clientManagement.table.noData' | translate }}</td>
        </tr>
      </table>
    </div>

    <app-overview-pagination
      [pageIndex]="pageIndex()"
      [pageSize]="pageSize()"
      [totalCount]="filteredCustomers().length"
      [pageSizeOptions]="[10, 25, 50]"
      (pageIndexChange)="pageIndex.set($event)"
      (pageSizeChange)="onPageSizeChange($event)"
    />
  }
</section>

<ng-template #createFormTemplate>
  <form [formGroup]="createForm" class="create-customer-form">
    <mat-form-field appearance="fill" class="create-customer-form__field">
      <mat-label>{{ 'clientManagement.createModal.name' | translate }}</mat-label>
      <input matInput formControlName="name" />
      @if (createForm.controls.name.touched && createForm.controls.name.hasError('required')) {
        <mat-error>{{ 'clientManagement.createModal.nameRequired' | translate }}</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="fill" class="create-customer-form__field">
      <mat-label>{{ 'clientManagement.createModal.azureTenantId' | translate }}</mat-label>
      <input matInput formControlName="azureTenantId" />
      @if (createForm.controls.azureTenantId.touched && createForm.controls.azureTenantId.hasError('required')) {
        <mat-error>{{ 'clientManagement.createModal.azureTenantIdRequired' | translate }}</mat-error>
      }
    </mat-form-field>

    <div class="create-customer-form__hosting-mode">
      <p class="create-customer-form__hosting-mode-label">
        {{ 'clientManagement.createModal.hostingMode' | translate }}
      </p>
      <mat-radio-group formControlName="hostingMode" class="create-customer-form__radio-group">
        <div class="create-customer-form__radio-option">
          <mat-radio-button value="MANAGED">
            {{ 'clientManagement.hostingMode.MANAGED' | translate }}
          </mat-radio-button>
          <p class="create-customer-form__radio-hint">
            {{ 'clientManagement.createModal.managedHint' | translate }}
          </p>
        </div>
        <div class="create-customer-form__radio-option">
          <mat-radio-button value="SELF_HOSTED">
            {{ 'clientManagement.hostingMode.SELF_HOSTED' | translate }}
          </mat-radio-button>
          <p class="create-customer-form__radio-hint">
            {{ 'clientManagement.createModal.selfHostedHint' | translate }}
          </p>
        </div>
      </mat-radio-group>
      @if (createForm.controls.hostingMode.touched && createForm.controls.hostingMode.hasError('required')) {
        <p>{{ 'clientManagement.createModal.hostingModeRequired' | translate }}</p>
      }
    </div>
  </form>
</ng-template>

<ng-template #editFormTemplate>
  <form [formGroup]="editForm" class="create-customer-form">
    <mat-form-field appearance="fill" class="create-customer-form__field">
      <mat-label>{{ 'clientManagement.createModal.name' | translate }}</mat-label>
      <input matInput formControlName="name" />
      @if (editForm.controls.name.touched && editForm.controls.name.hasError('required')) {
        <mat-error>{{ 'clientManagement.createModal.nameRequired' | translate }}</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="fill" class="create-customer-form__field">
      <mat-label>{{ 'clientManagement.createModal.azureTenantId' | translate }}</mat-label>
      <input matInput formControlName="azureTenantId" />
      <mat-hint>{{ 'clientManagement.createModal.azureTenantIdHint' | translate }}</mat-hint>
    </mat-form-field>

    <div class="create-customer-form__hosting-mode">
      <p class="create-customer-form__hosting-mode-label">
        {{ 'clientManagement.table.status' | translate }}
      </p>
      <mat-radio-group formControlName="status" class="create-customer-form__radio-group">
        @for (option of statusOptions; track option) {
          <mat-radio-button [value]="option">
            {{ 'clientManagement.status.' + option | translate }}
          </mat-radio-button>
        }
      </mat-radio-group>
    </div>

    <mat-form-field appearance="fill" class="create-customer-form__field">
      <mat-label>{{ 'clientManagement.table.hostingMode' | translate }}</mat-label>
      <input
        matInput
        [value]="'clientManagement.hostingMode.' + selectedCustomer()?.hostingMode | translate"
        disabled
      />
    </mat-form-field>
  </form>
</ng-template>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="showCreateModal()"
  [title]="'clientManagement.createModal.title' | translate"
  [contentTemplate]="createFormTemplate"
  [saveText]="'clientManagement.createModal.save' | translate"
  [cancelText]="'clientManagement.createModal.cancel' | translate"
  [disabled]="createForm.invalid || createSaving()"
  (save)="onCreateSave()"
  (cancel)="onCreateCancel()"
  (visibleChange)="showCreateModal.set($event)"
></lib-modal>

<ng-template #deleteConfirmTemplate>
  <p>{{ 'clientManagement.deleteModal.confirmation' | translate }}</p>
</ng-template>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="showDeleteModal()"
  [title]="'clientManagement.deleteModal.title' | translate"
  [contentTemplate]="deleteConfirmTemplate"
  [saveText]="'clientManagement.deleteModal.confirm' | translate"
  [cancelText]="'clientManagement.deleteModal.cancel' | translate"
  [disabled]="deleteSaving()"
  (save)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()"
  (visibleChange)="showDeleteModal.set($event)"
></lib-modal>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="showEditModal()"
  [title]="'clientManagement.editModal.title' | translate"
  [contentTemplate]="editFormTemplate"
  [saveText]="'clientManagement.editModal.save' | translate"
  [cancelText]="'clientManagement.editModal.cancel' | translate"
  [disabled]="editSaving() || editForm.invalid || !editForm.dirty"
  (save)="onEditSave()"
  (cancel)="onEditCancel()"
  (visibleChange)="showEditModal.set($event)"
></lib-modal>
